import { FC, ReactNode } from "react";

import Box, { BoxProps } from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { GridProps } from "@mui/material/Grid";

import Button from "components/CustomButton";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Image from "components/Image";

import styles from "./styles";

interface ContentSectionProps {
  title?: ReactNode;
  text?: ReactNode;
  buttonAction?: () => void;
  cta?: ReactNode;
  ctaPath?: string;
  cta2?: {
    text: string;
    target?: string;
    path?: string;
    onClick?: () => void;
  };
  reverse?: boolean;
  imagePath?: string;
  imageWidth?: number;
  imageHeight?: number;
  video?: ReactNode;
  counter?: ReactNode;
  boxProps?: BoxProps;
  gridContainerProps?: GridProps;
  imageBoxProps?: BoxProps;
  children?: ReactNode;
}

const ContentSection: FC<ContentSectionProps> = ({
  title,
  text,
  buttonAction,
  cta,
  ctaPath,
  cta2 = null,
  reverse = false,
  imagePath = "",
  imageWidth = 460,
  imageHeight = 540,
  video,
  counter = null,
  boxProps = {},
  gridContainerProps = {},
  imageBoxProps = {},
  children,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{ py: { xs: 4, lg: 8 } }}
      {...boxProps}
    >
      <GridContainer
        spacing={0}
        display="flex"
        alignItems="center"
        direction={reverse ? "row-reverse" : "row"}
        sx={reverse ? styles.reverse : {}}
        {...gridContainerProps}
      >
        <GridItem xs={12} sm={12} lg={6}>
          {children?.[0] ? (
            <Box width="100%">{children?.[0]}</Box>
          ) : (
            <>
              {title}
              <Box mt={4} mb={counter ? 3 : 4}>
                {text}
              </Box>
              {(cta || cta2 || counter) && (
                <Box
                  display="flex"
                  justifyContent={reverse ? "flex-end" : "flex-start"}
                  width="100%"
                >
                  {cta && (
                    <Button
                      href={ctaPath}
                      onClick={buttonAction}
                      fullWidth={isMobile}
                    >
                      {cta}
                    </Button>
                  )}
                  {cta2 && (
                    <Button
                      sx={{ ml: 2 }}
                      fullWidth={isMobile}
                      target={cta2.target ?? "_blank"}
                      href={cta2.path}
                      onClick={cta2.onClick}
                      color="secondary"
                    >
                      {cta2.text}
                    </Button>
                  )}
                  {counter}
                </Box>
              )}
            </>
          )}
        </GridItem>

        <GridItem
          xs={12}
          sm={12}
          lg={6}
          display="flex"
          alignItems={video ? "center" : undefined}
        >
          {children?.[1] ? (
            <Box width="100%">{children[1]}</Box>
          ) : (
            <Box
              display="flex"
              justifyContent={reverse ? "flex-start" : "flex-end"}
              width="100%"
              {...imageBoxProps}
              sx={[
                {
                  marginTop: { xs: 2, lg: 0 },
                },
                styles.imageBox,
                ...(Array.isArray(imageBoxProps.sx)
                  ? imageBoxProps.sx || {}
                  : [imageBoxProps.sx || {}]),
              ]}
            >
              {video || (
                <Image
                  src={imagePath}
                  alt=""
                  width={imageWidth}
                  height={imageHeight}
                />
              )}
            </Box>
          )}
        </GridItem>
      </GridContainer>
    </Box>
  );
};

export default ContentSection;
